/**
 * Push Notifications Hook
 * Custom React hook for managing push notifications state and actions
 */

import { useState, useEffect, useCallback } from 'react';
import {
  isPushNotificationSupported,
  getNotificationPermissionStatus,
  requestNotificationPermission,
  scheduleDailyPrayerReminders,
  clearAllNotifications,
  getNotificationSettings,
  sendTestNotification,
  type NotificationPermissionStatus
} from '../services/pushNotifications';
import { logger } from '../utils/logger';

export interface NotificationTime {
  hour: number;
  minute: number;
  label?: string;
}

export interface PushNotificationState {
  isSupported: boolean;
  permission: NotificationPermissionStatus | null;
  isEnabled: boolean;
  reminderTimes: NotificationTime[];
  isLoading: boolean;
  error: string | null;
}

export interface PushNotificationActions {
  requestPermission: () => Promise<boolean>;
  enableNotifications: (times: NotificationTime[]) => Promise<void>;
  disableNotifications: () => Promise<void>;
  updateReminderTimes: (times: NotificationTime[]) => Promise<void>;
  sendTest: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const usePushNotifications = (): [PushNotificationState, PushNotificationActions] => {
  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    permission: null,
    isEnabled: false,
    reminderTimes: [],
    isLoading: true,
    error: null
  });

  /**
   * Initialize push notification state
   */
  const initialize = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Check if notifications are supported
      const supported = isPushNotificationSupported();

      // Get current permission status
      const permission = await getNotificationPermissionStatus();

      // Get current settings
      const settings = await getNotificationSettings();

      setState(prev => ({
        ...prev,
        isSupported: supported,
        permission,
        isEnabled: settings.enabled && permission.granted,
        reminderTimes: settings.times.map(time => ({
          hour: time.hour,
          minute: time.minute,
          label: formatTimeLabel(time.hour, time.minute)
        })),
        isLoading: false
      }));

      logger.debug('Push notifications initialized', {
        supported,
        permission,
        settings
      });

    } catch (error) {
      logger.error('Error initializing push notifications', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to initialize notifications'
      }));
    }
  }, []);

  /**
   * Request notification permission
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const granted = await requestNotificationPermission();
      const permission = await getNotificationPermissionStatus();

      setState(prev => ({
        ...prev,
        permission,
        isLoading: false
      }));

      logger.debug('Permission request result:', granted);
      return granted;

    } catch (error) {
      logger.error('Error requesting notification permission', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to request permission'
      }));
      return false;
    }
  }, []);

  /**
   * Enable notifications with specified times
   */
  const enableNotifications = useCallback(async (times: NotificationTime[]): Promise<void> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      // Ensure we have permission first
      const permission = await getNotificationPermissionStatus();
      if (!permission.granted) {
        const granted = await requestNotificationPermission();
        if (!granted) {
          throw new Error('Permission not granted');
        }
      }

      // Schedule notifications
      await scheduleDailyPrayerReminders(times);

      setState(prev => ({
        ...prev,
        isEnabled: true,
        reminderTimes: times.map(time => ({
          ...time,
          label: formatTimeLabel(time.hour, time.minute)
        })),
        isLoading: false
      }));

      logger.debug('Notifications enabled with times:', times);

    } catch (error) {
      logger.error('Error enabling notifications', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to enable notifications'
      }));
    }
  }, []);

  /**
   * Disable all notifications
   */
  const disableNotifications = useCallback(async (): Promise<void> => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      await clearAllNotifications();

      setState(prev => ({
        ...prev,
        isEnabled: false,
        reminderTimes: [],
        isLoading: false
      }));

      logger.debug('Notifications disabled');

    } catch (error) {
      logger.error('Error disabling notifications', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to disable notifications'
      }));
    }
  }, []);

  /**
   * Update reminder times
   */
  const updateReminderTimes = useCallback(async (times: NotificationTime[]): Promise<void> => {
    if (state.isEnabled) {
      await enableNotifications(times);
    }
  }, [state.isEnabled, enableNotifications]);

  /**
   * Send test notification
   */
  const sendTest = useCallback(async (): Promise<void> => {
    try {
      await sendTestNotification();
      logger.debug('Test notification sent');
    } catch (error) {
      logger.error('Error sending test notification', error);
    }
  }, []);

  /**
   * Refresh notification state
   */
  const refresh = useCallback(async (): Promise<void> => {
    await initialize();
  }, [initialize]);

  // Initialize on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  const actions: PushNotificationActions = {
    requestPermission,
    enableNotifications,
    disableNotifications,
    updateReminderTimes,
    sendTest,
    refresh
  };

  return [state, actions];
};

/**
 * Format time label for display
 */
const formatTimeLabel = (hour: number, minute: number): string => {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const displayMinute = minute.toString().padStart(2, '0');
  return `${displayHour}:${displayMinute} ${period}`;
};

/**
 * Common prayer reminder times
 */
export const COMMON_PRAYER_TIMES: NotificationTime[] = [
  { hour: 6, minute: 0, label: '6:00 AM - Morning Prayer' },
  { hour: 12, minute: 0, label: '12:00 PM - Midday Prayer' },
  { hour: 18, minute: 0, label: '6:00 PM - Evening Prayer' },
  { hour: 21, minute: 0, label: '9:00 PM - Night Prayer' }
];

/**
 * Generate time options for selection
 */
export const generateTimeOptions = (): NotificationTime[] => {
  const times: NotificationTime[] = [];

  for (let hour = 5; hour <= 23; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      times.push({
        hour,
        minute,
        label: formatTimeLabel(hour, minute)
      });
    }
  }

  return times;
};